// Pitch math + key-signature transforms. Sits below inferKey and above
// the agent recipes — pure functions, no AST awareness.
//
// The single source of truth for pitch names is blocks/tonal/parse.ts:
// ROOTS is the chromatic order, ROOT_ALIASES collapses sharps/flats to
// the canonical spelling. Everything here goes through those.

import { canonicalRoot, ROOTS, SCALE_MODES } from '../blocks/tonal/parse';

export type KeyMode = (typeof SCALE_MODES)[number];

/** Semitone index 0..11 of the canonical root, or null if the name
 *  isn't a root we recognise. */
export function rootToSemitone(name: string): number | null {
  const canon = canonicalRoot(name);
  if (!canon) return null;
  const idx = (ROOTS as readonly string[]).indexOf(canon);
  return idx >= 0 ? idx : null;
}

/** Inverse of rootToSemitone. Always returns a canonical spelling. */
export function semitoneToRoot(semitone: number): string {
  const wrapped = ((semitone % 12) + 12) % 12;
  return ROOTS[wrapped]!;
}

/** Major ↔ relative minor: same pitch set, root drops 3 semitones for
 *  major→minor, climbs 3 for minor→major. Other modes are passed
 *  through unchanged because their "relative" isn't standard usage. */
export function relativeOf(
  root: string,
  mode: KeyMode,
): { root: string; mode: KeyMode } | null {
  const semi = rootToSemitone(root);
  if (semi === null) return null;
  if (mode === 'major') {
    return { root: semitoneToRoot(semi - 3), mode: 'minor' };
  }
  if (mode === 'minor' || mode === 'aeolian') {
    return { root: semitoneToRoot(semi + 3), mode: 'major' };
  }
  return null;
}

/** Format `{root, mode}` as a Strudel scale string ("C minor"). */
export function formatScale(root: string, mode: KeyMode): string {
  return `${root} ${mode}`;
}

/** True when `mode` is in our recognised set. Cheap guard for caller
 *  inputs that come from the AST (where the field is a bare string). */
export function isKnownMode(mode: string): mode is KeyMode {
  return (SCALE_MODES as readonly string[]).includes(mode);
}
